import { defineStore } from 'pinia';
import {computed, ref} from 'vue';
import { Services } from 'src/services/service-manager';
import { CodeType } from 'src/contracts/codeType';
import {useGrouperStore} from 'stores/grouper-store';
import { SearchService } from 'src/services/search-service';
import { DrgAnalysisCode } from 'src/contracts/drgAnalysis';
import {
  BookViewRecord, CodebookPCSTableResponse,
  SearchViewRecord,
  TrucodeSearchService
} from 'src/services/TrucodeSearchService';

const trucodeSearchService = Services.service(TrucodeSearchService);

export type SearchMode = 'index'|'table'|'';

export const CodeBookDropdownOptions = [
  'Diagnosis, ICD-10-CM',
  'External Cause, ICD-10-CM',
  'Procedure, ICD-10-PCS',
];

const codeBookLookupMap = new Map<string, string> ([
  ['Diagnosis, ICD-10-CM', 'ICD10CM_DX'],
  ['External Cause, ICD-10-CM', 'ICD10CM_E'],
  ['Procedure, ICD-10-PCS', 'ICD10PCS_PR']
]);

export const useSearchStore = defineStore('searchStore', () => {

  const bookSelected = ref(CodeBookDropdownOptions[0]);
  const getBookSelected = computed(() => bookSelected.value);
  const isProceduresBookSelected = computed(() => bookSelected.value === 'Procedure, ICD-10-PCS');
  const getBookTitle = computed(() => {
    if (CodeBookDropdownOptions.includes(bookSelected.value)) {
      const bookNameSplit = bookSelected.value.split(', ');
      const bookTitle = bookNameSplit.length > 1 ? bookNameSplit[1] +  ' ' + bookNameSplit[0] : bookSelected.value;
      return bookTitle + ' Index';
    } else {
      return '';
    }
  });
  const modeSelected = ref<SearchMode>('');
  const isTableModeSelected = computed(() => modeSelected.value == 'table');

  const searchResults = ref<SearchViewRecord[] | undefined>([]);
  const searchResultDetails = ref(<BookViewRecord[] | undefined>[]);
  const searchTable = ref<CodebookPCSTableResponse | undefined>();

  function setBookSelected(book: string) {
    bookSelected.value = book;
    //if procedures book is selected mode should be set to default (index), otherwise mode cannot be set and should be empty
    setModeSelected(isProceduresBookSelected.value ? 'index' : '');
    resetSearch();
  }

  function setModeSelected(mode: SearchMode) {
    modeSelected.value = mode;
  }

  async function getCodebookTermSearchAndInitialDetails(term: string): Promise<SearchViewRecord[] | undefined> {
    const bookId = codeBookLookupMap.get(bookSelected.value);
    if (bookId){
      const searchResults = await getCodebookTermSearch(bookId, term);
      if (searchResults.length > 0) {
        const firstRecord = searchResults[0];
        if (firstRecord) {
          await getResultDetails(firstRecord);
        }
      }
    }
    return searchResults.value;
  }

  async function getResultDetails(result: SearchViewRecord){
    const bookId = codeBookLookupMap.get(bookSelected.value);
    if (bookId){
      const code = result.codes.length > 0 ? result.codes[0] : '';
      if (!isProceduresBookSelected.value){
        if (code !== ''){
          await getCodebookDetails(bookId + '_TABULAR', result.data_sequence);
        } else {
          await getCodebookDetails(bookId + '_INDEX', result.data_sequence);
        }
      } else {
        const partialCodeArray = result.text.match(/^.*<a href.*?>([A-Z0-9]+)<\/a>?/) || [];
        const partialCode = partialCodeArray.length > 1 ? partialCodeArray[1] : '';
        const procCode = code === '' ? partialCode : code;
        if (procCode !== '') {
          await getPCSTable(procCode[0]);
        } else {
          await getCodebookDetails(bookId + '_INDEX', result.data_sequence);
        }
      }
    }
  }

  async function getCodebookTermSearch(bookId: string,  term: string): Promise<SearchViewRecord[] | []> {

    const results =  await trucodeSearchService.getCodebooksSearch(bookId, term, useGrouperStore().patientEffectiveDate)
    searchResults.value = results;

    return results;
  }

  async function getCodebookDetails( bookId: string, sequenceId: number): Promise<BookViewRecord[] | undefined> {
    const resultDetails = await trucodeSearchService.getCodebooksDetails(bookId, sequenceId, useGrouperStore().patientEffectiveDate);
    searchResultDetails.value = resultDetails;

    return resultDetails;
  }

  async function getPCSTable(code: string): Promise<CodebookPCSTableResponse | undefined> {
    const resultTable = await trucodeSearchService.getCodebooksPCSTable(code, useGrouperStore().patientEffectiveDate);
    searchTable.value = resultTable;

    return resultTable;
  }

  function resetSearch(){
    searchResults.value = [];
    searchResultDetails.value = [];
    searchTable.value = undefined;
  }

  async function addDxOrProcCode(code: string){
    try {
      if (isProceduresBookSelected.value){
        await useGrouperStore().addProcedureCode(code, undefined, true);
      } else {
        await useGrouperStore().addDiagnosisCode(code, undefined, undefined, true);
      }
    } catch {
      //TODO, what should happen if code isn't added?
      console.log('Something went wrong, ' + code + ' was not added');
    }
  }

  async function quickAddCodeSearch(text: string, codeType: CodeType): Promise<DrgAnalysisCode|undefined> {

    const searchService = Services.service(SearchService);
    const codes = await searchService.searchCodes(text, codeType);

    text = cleanText(text);
    return codes?.find(code => cleanText(code.value) === text);
  }

  function cleanText(text: string): string {
    return text.replace('.', '').toLowerCase().trim();
  }

  return {
    bookSelected,
    getBookSelected,
    isProceduresBookSelected,
    getBookTitle,
    modeSelected,
    isTableModeSelected,
    searchResults,
    searchResultDetails,
    searchTable,
    setBookSelected,
    setModeSelected,
    getCodebookTermSearchAndInitialDetails,
    getResultDetails,
    getCodebookTermSearch,
    getCodebookDetails,
    getPCSTable,
    addDxOrProcCode,
    quickAddCodeSearch
  }
});
